import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import { Bot, Send } from "lucide-react";
import useStreamingChat from "./assistant/useStreamingChat";
import MessageList from "./assistant/MessageList";

const SUGGESTIONS = [
  "How would APT29 chain T1078 + T1133 against hybrid AD?",
  "Write a Sigma rule that catches Cobalt Strike beacon sleep masks.",
  "Walk me through a 2-hour purple-team Kerberoasting tabletop.",
  "Which AMSI bypass variant evades Defender's latest signatures?",
];

const DEMO_STARTER = "Brief me red-team style: how would a Volt Typhoon-class actor establish persistence in a hybrid Azure/AD environment, and which DefDossier detections + exercises map to each step?";

export default function Assistant() {
  const { messages, busy, send, loaded, followups, followupsLoading } = useStreamingChat();
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  const [params] = useSearchParams();
  const autoStarted = useRef(false);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // Auto-fire the demo starter exactly once when the URL has ?demo=1.
  // Ref-guarded so query-param URL cleanup is unnecessary.
  useEffect(() => {
    if (autoStarted.current) return;
    if (params.get("demo") !== "1") return;
    if (!loaded) return;
    if (busy) return;
    if (messages.length > 0) return;
    autoStarted.current = true;
    send(DEMO_STARTER);
  }, [params, loaded, busy, messages.length, send]);

  const onSend = (text) => {
    if (busy) return;
    send(text ?? input);
    setInput("");
  };

  const last = messages[messages.length - 1];
  const showConnecting = busy && last?.streaming && last?.content === "";

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Bot size={20} color="var(--cyan)" strokeWidth={1.5} />
          <div className="overline">// SENTINEL_AI / claude-sonnet-4.5 / streaming</div>
        </div>
        <h1 className="font-display font-bold text-4xl uppercase mb-2" data-testid="assistant-title">Sentinel AI Training Assistant</h1>
        <p className="text-[var(--text-2)] mb-6">Purple-team grade Q&amp;A — adversary emulation, detection engineering, threat hunting, and leadership.</p>

        <div className="flat-card p-0">
          <div className="border-b border-[var(--border)] px-4 py-2 font-mono text-xs text-[var(--text-2)] flex items-center justify-between">
            <span><span className="text-[var(--green)]">●</span> SECURE_SHELL // sentinel@defdossier</span>
            <span>{messages.length} TURNS</span>
          </div>

          <MessageList
            messages={messages}
            suggestions={SUGGESTIONS}
            followups={followups}
            followupsLoading={followupsLoading}
            onPick={onSend}
            endRef={endRef}
            showConnecting={showConnecting}
            busy={busy}
          />

          <form
            onSubmit={(e) => { e.preventDefault(); onSend(); }}
            className="border-t border-[var(--border)] p-3 flex gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="input-tac flex-1 font-mono"
              placeholder="ask SENTINEL anything…"
              data-testid="chat-input"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} className="btn-primary inline-flex items-center gap-2" data-testid="chat-send">
              <Send size={16} /> Send
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
